import React, { useEffect, useState } from "react";
import axios from "axios";
import ReactPlayer from "react-player";
import {
  Alert,
  Badge,
  Button,
  Card,
  Col,
  Empty,
  Image,
  Modal,
  Row,
  Skeleton,
  Space,
  Tag,
  Typography,
} from "antd";
import { EditOutlined, PlayCircleOutlined } from "@ant-design/icons";
import { useTranslation } from "react-i18next";
import Constants from "../Utils/Constants";

const isMinioImageKey = (value) =>
  typeof value === "string" && value.startsWith("images/");

const isMinioVideoKey = (value) =>
  typeof value === "string" && value.startsWith("videos/");

const normalizeLegacyUrl = (url) => {
  if (!url) return null;

  if (url.startsWith("http")) {
    return url;
  }

  return `${Constants.SERVER_URL.replace(/\/$/, "")}/${url.replace(
    /^\/+/,
    ""
  )}`;
};

export default function BlocViewModal({ open, blocKey, onClose, onEdit }) {
  const { t, i18n } = useTranslation("Blocs2");
  const token = localStorage.getItem("token");

  const headers = {
    Authorization: "Bearer " + token,
    "Accept-Language": i18n.language,
  };

  const [bloc, setBloc] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  const [imageUrls, setImageUrls] = useState({});
  const [videoUrls, setVideoUrls] = useState({});

  const [videoPreviewOpen, setVideoPreviewOpen] = useState(false);
  const [videoPreviewUrl, setVideoPreviewUrl] = useState(null);

  const getSignedUrl = async (key, bucket) => {
    const res = await axios.get(
      `${Constants.SERVER_URL}/api/minio/presign-download`,
      {
        params: {
          bucket,
          key,
        },
        headers,
      }
    );

    return res.data.downloadUrl;
  };

  const openVideoPreview = (url) => {
    if (!url) return;

    setVideoPreviewUrl(url);
    setVideoPreviewOpen(true);
  };

  useEffect(() => {
    if (!open || !blocKey) {
      setBloc(null);
      setError(null);
      setImageUrls({});
      setVideoUrls({});
      setVideoPreviewOpen(false);
      setVideoPreviewUrl(null);
      return;
    }

    setLoading(true);
    setError(null);
    setBloc(null);
    setImageUrls({});
    setVideoUrls({});
    setVideoPreviewOpen(false);
    setVideoPreviewUrl(null);

    axios
      .get(`${Constants.SERVER_URL}/bloc/${encodeURIComponent(blocKey)}`, {
        headers,
      })
      .then((res) => {
        setBloc(res.data);
      })
      .catch((err) => {
        setError(err);
      })
      .finally(() => {
        setLoading(false);
      });

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, blocKey]);

  useEffect(() => {
    const exercises = bloc?.exercises || [];

    if (!exercises.length) {
      setImageUrls({});
      setVideoUrls({});
      return;
    }

    let cancelled = false;

    async function loadMedia() {
      const imageEntries = await Promise.all(
        exercises.map(async (exercise) => {
          if (!exercise.imageUrl) {
            return [exercise.id, null];
          }

          try {
            if (isMinioImageKey(exercise.imageUrl)) {
              const signedImageUrl = await getSignedUrl(
                exercise.imageUrl,
                "rxapa-assets"
              );

              return [exercise.id, signedImageUrl];
            }

            return [exercise.id, normalizeLegacyUrl(exercise.imageUrl)];
          } catch {
            return [exercise.id, null];
          }
        })
      );

      const videoEntries = await Promise.all(
        exercises.map(async (exercise) => {
          if (!exercise.videoUrl) {
            return [exercise.id, null];
          }

          try {
            if (isMinioVideoKey(exercise.videoUrl)) {
              const signedVideoUrl = await getSignedUrl(
                exercise.videoUrl,
                "rxapa-videos"
              );

              return [exercise.id, signedVideoUrl];
            }

            return [exercise.id, normalizeLegacyUrl(exercise.videoUrl)];
          } catch {
            return [exercise.id, null];
          }
        })
      );

      if (!cancelled) {
        setImageUrls(Object.fromEntries(imageEntries));
        setVideoUrls(Object.fromEntries(videoEntries));
      }
    }

    loadMedia();

    return () => {
      cancelled = true;
    };

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [bloc]);

  const exercises = bloc?.exercises || [];

  const requiredLabel = t("label_required", {
    defaultValue: "Obligatoire",
  });

  const optionalLabel = t("label_optional", {
    defaultValue: "Optionnel",
  });

  const viewVideoLabel = t("label_view_video", {
    defaultValue: "Voir la vidéo",
  });

  const labelDescription = t("label_description", {
    defaultValue: "Description",
  });

  const labelExercises = t("label_bloc_exercises", {
    defaultValue: "Exercices du bloc",
  });

  const labelExercise = t("label_exercise", {
    defaultValue: "Exercice",
  });

  const renderInstructionTag = (label, value) => (
    <Tag>
      <strong style={{ marginRight: 6 }}>{label}</strong>
      {value ?? "-"}
    </Tag>
  );

  return (
    <>
      <Modal
        open={open}
        onCancel={onClose}
        title={
          bloc?.name
            ? `${t("label_view_bloc_title", {
                defaultValue: "Visualisation du bloc",
              })} : ${bloc.name}`
            : t("label_view_bloc_title", {
                defaultValue: "Visualisation du bloc",
              })
        }
        width={900}
        destroyOnClose
        footer={[
          <Button key="close" onClick={onClose}>
            {t("button_close", { defaultValue: "Fermer" })}
          </Button>,

          <Button
            key="edit"
            type="primary"
            icon={<EditOutlined />}
            onClick={() => onEdit?.(blocKey)}
          >
            {t("label_view_edit_button", { defaultValue: "Modifier" })}
          </Button>,
        ]}
      >
        {loading ? (
          <Skeleton active paragraph={{ rows: 8 }} />
        ) : error ? (
          <Alert
            type="error"
            message={t("label_view_error", {
              defaultValue: "Erreur lors du chargement du bloc.",
            })}
          />
        ) : !bloc ? (
          <Alert
            type="warning"
            message={t("label_view_not_found", {
              defaultValue: "Bloc introuvable.",
            })}
          />
        ) : (
          <Space direction="vertical" size={20} style={{ width: "100%" }}>
            <Card
              style={{
                background: "#0f5b78",
                borderRadius: 10,
              }}
              styles={{
                body: {
                  padding: 18,
                },
              }}
            >
              <Typography.Text
                style={{
                  color: "rgba(255,255,255,0.9)",
                  fontWeight: 600,
                }}
              >
                {labelDescription}
              </Typography.Text>

              <Typography.Paragraph
                style={{
                  color: "white",
                  marginBottom: 0,
                  marginTop: 8,
                }}
              >
                {bloc.description || "-"}
              </Typography.Paragraph>
            </Card>

            <Typography.Title level={5} style={{ marginBottom: 0 }}>
              {labelExercises} ({exercises.length})
            </Typography.Title>

            {exercises.length === 0 ? (
              <Empty
                description={t("label_no_exercises", {
                  defaultValue: "Aucun exercice.",
                })}
              />
            ) : (
              exercises.map((exercise, index) => {
                const pivot = exercise.Exercise_Bloc || {};
                const required = !!pivot.required;
                const ribbonText = required ? requiredLabel : optionalLabel;
                const ribbonColor = required ? "red" : "blue";

                const seriesLabel = t("label_series", {
                  defaultValue: "Séries",
                });

                const repsLabel = t("label_reps", {
                  defaultValue: "Répétitions",
                });

                const minutesLabel = t("label_minutes", {
                  defaultValue: "Minutes",
                });

                const restLabel = t("label_rest", {
                  defaultValue: "Repos",
                });

                return (
                  <div key={exercise.id}>
                    <Typography.Text
                      type="secondary"
                      style={{ display: "block" }}
                    >
                      {labelExercise} {index + 1}
                    </Typography.Text>

                    <Badge.Ribbon text={ribbonText} color={ribbonColor}>
                      <Card
                        size="small"
                        style={{
                          borderRadius: 10,
                          marginTop: 8,
                        }}
                        styles={{
                          body: {
                            padding: 16,
                          },
                        }}
                      >
                        <Row gutter={[16, 12]} wrap>
                          <Col flex="180px">
                            {imageUrls[exercise.id] ? (
                              <Image
                                src={imageUrls[exercise.id]}
                                alt={exercise.name}
                                width={180}
                                height={140}
                                style={{
                                  objectFit: "cover",
                                  borderRadius: 8,
                                }}
                                preview
                                onError={(event) => {
                                  event.currentTarget.style.display = "none";
                                }}
                              />
                            ) : (
                              <div
                                style={{
                                  width: 180,
                                  height: 140,
                                  borderRadius: 8,
                                  background: "#0f5b78",
                                  display: "flex",
                                  alignItems: "center",
                                  justifyContent: "center",
                                  color: "white",
                                  fontWeight: 700,
                                }}
                              >
                                image
                              </div>
                            )}
                          </Col>

                          <Col flex="auto" style={{ minWidth: 260 }}>
                            <Space
                              direction="vertical"
                              size={12}
                              style={{ width: "100%" }}
                            >
                              <div>
                                <Typography.Title
                                  level={5}
                                  style={{ margin: 0 }}
                                >
                                  {exercise.name || "-"}
                                </Typography.Title>

                                <Space size={8} wrap style={{ marginTop: 6 }}>
                                  {exercise.category ? (
                                    <Tag>{exercise.category}</Tag>
                                  ) : null}

                                  {exercise.fitnessLevel ? (
                                    <Tag>{exercise.fitnessLevel}</Tag>
                                  ) : null}
                                </Space>
                              </div>

                              {exercise.description ? (
                                <Typography.Paragraph
                                  style={{
                                    marginBottom: 0,
                                    color: "#374151",
                                  }}
                                >
                                  {exercise.description}
                                </Typography.Paragraph>
                              ) : null}

                              <div>
                                <Typography.Text
                                  type="secondary"
                                  style={{
                                    fontWeight: 600,
                                    display: "block",
                                    marginBottom: 8,
                                  }}
                                >
                                  {t("label_instructions", {
                                    defaultValue: "Instructions",
                                  })}
                                </Typography.Text>

                                <Space size={[8, 8]} wrap>
                                  {renderInstructionTag(
                                    seriesLabel,
                                    pivot.numberOfSeries ?? "-"
                                  )}

                                  {renderInstructionTag(
                                    repsLabel,
                                    pivot.numberOfRepetition ?? "-"
                                  )}

                                  {renderInstructionTag(
                                    minutesLabel,
                                    pivot.minutes ?? "-"
                                  )}

                                  {renderInstructionTag(
                                    restLabel,
                                    pivot.restingInstruction || "-"
                                  )}
                                </Space>
                              </div>

                              {videoUrls[exercise.id] ? (
                                <Row justify="end">
                                  <Typography.Link
                                    onClick={() =>
                                      openVideoPreview(videoUrls[exercise.id])
                                    }
                                  >
                                    <PlayCircleOutlined
                                      style={{ marginRight: 6 }}
                                    />
                                    {viewVideoLabel}
                                  </Typography.Link>
                                </Row>
                              ) : null}
                            </Space>
                          </Col>
                        </Row>
                      </Card>
                    </Badge.Ribbon>
                  </div>
                );
              })
            )}
          </Space>
        )}
      </Modal>

      <Modal
        open={videoPreviewOpen}
        onCancel={() => {
          setVideoPreviewOpen(false);
          setVideoPreviewUrl(null);
        }}
        footer={null}
        title="Aperçu vidéo"
        width={900}
        destroyOnClose
      >
        {videoPreviewUrl ? (
          <div style={{ aspectRatio: "16/9" }}>
            <ReactPlayer
              url={videoPreviewUrl}
              controls
              width="100%"
              height="100%"
            />
          </div>
        ) : (
          <Empty description="Impossible d’afficher la vidéo." />
        )}
      </Modal>
    </>
  );
}